import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { TrendingUp } from "lucide-react";

const LEVEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];
const PALETTE = ["#4F8CFF", "#34d399", "#fbbf24", "#f472b6", "#a78bfa", "#22d3ee", "#fb923c", "#e879f9", "#86efac", "#93c5fd", "#fcd34d", "#f87171"];

export function MasteryTimeline({ onSelect }) {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [hidden, setHidden] = useState(new Set());

  useEffect(() => { api.get(`/knowledge/timeline?days=${days}`).then(r => setData(r.data)).catch(() => {}); }, [days]);

  const W = 960, H = 340, padL = 92, padR = 24, padT = 16, padB = 36;
  const dates = data?.dates || [];
  const series = useMemo(() => (data?.series || []).filter(s => s.end > 0 || s.gain > 0), [data]);
  const x = (i) => padL + (i / Math.max(1, dates.length - 1)) * (W - padL - padR);
  const y = (lvl) => padT + (1 - lvl / 5) * (H - padT - padB);

  // stagger overlapping identical paths slightly so lines don't hide each other
  const path = (pts, idx) => pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${(y(p) + ((idx % 5) - 2) * 1.6).toFixed(1)}`).join(" ");

  if (!data) return <div className="aria-card p-8 text-sm text-[var(--aria-text-muted)]">Loading timeline…</div>;

  return (
    <div className="aria-card p-5" data-testid="mastery-timeline">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2 text-xs text-[var(--aria-text-muted)]">
          <TrendingUp size={14} className="text-[var(--aria-primary)]" />
          <span data-testid="timeline-summary"><b className="text-[var(--aria-text)]">{data.summary.improved}</b> of {data.summary.concepts} concepts climbed · <b className="text-emerald-400">+{data.summary.levels_gained}</b> levels in {days} days</span>
        </div>
        <div className="flex rounded-md border border-[var(--aria-border)] overflow-hidden text-xs" data-testid="timeline-range">
          {[[14, "2w"], [28, "4w"], [56, "8w"], [112, "16w"]].map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`timeline-range-${d}`} className={`px-2.5 py-1 ${days === d ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>{l}</button>
          ))}
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="timeline-chart">
        {[0, 1, 2, 3, 4, 5].map(l => (
          <g key={l}>
            <line x1={padL} x2={W - padR} y1={y(l)} y2={y(l)} stroke="rgba(255,255,255,0.06)" />
            <text x={padL - 10} y={y(l) + 4} textAnchor="end" fontSize="11" fill="#7c88a8">{LEVEL[l]}</text>
          </g>
        ))}
        {dates.map((d, i) => (i % Math.ceil(dates.length / 8) === 0 || i === dates.length - 1) && (
          <text key={d} x={x(i)} y={H - 12} textAnchor="middle" fontSize="10" fill="#7c88a8">{new Date(d + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</text>
        ))}
        {series.map((s, idx) => {
          if (hidden.has(s.concept)) return null;
          const color = PALETTE[idx % PALETTE.length];
          const dim = active && active !== s.concept;
          return (
            <g key={s.concept} style={{ opacity: dim ? 0.12 : 1, transition: "opacity 200ms" }} onMouseEnter={() => setActive(s.concept)} onMouseLeave={() => setActive(null)} onClick={() => onSelect?.(s.concept)} className="cursor-pointer" data-testid={`timeline-line-${s.concept}`}>
              <path d={path(s.points, idx)} fill="none" stroke={color} strokeWidth={active === s.concept ? 3 : 2} strokeLinejoin="round" strokeLinecap="round" style={{ filter: active === s.concept ? `drop-shadow(0 0 6px ${color})` : "none" }} />
              <path d={path(s.points, idx)} fill="none" stroke="transparent" strokeWidth="14" />
              <circle cx={x(s.points.length - 1)} cy={y(s.end) + ((idx % 5) - 2) * 1.6} r={active === s.concept ? 5 : 3.5} fill="#030711" stroke={color} strokeWidth="2" />
            </g>
          );
        })}
        {active && (() => {
          const s = series.find(z => z.concept === active); if (!s) return null;
          return <text x={W - padR} y={padT + 4} textAnchor="end" fontSize="12" fill="#fff" fontWeight="600">{s.concept}: {LEVEL[s.start]} → {LEVEL[s.end]} {s.gain > 0 ? `(+${s.gain})` : ""}</text>;
        })()}
      </svg>

      <div className="flex flex-wrap gap-2 mt-3" data-testid="timeline-legend">
        {series.map((s, idx) => {
          const color = PALETTE[idx % PALETTE.length];
          const off = hidden.has(s.concept);
          return (
            <button key={s.concept} data-testid={`timeline-legend-${s.concept}`}
              onMouseEnter={() => setActive(s.concept)} onMouseLeave={() => setActive(null)}
              onClick={() => setHidden(h => { const n = new Set(h); n.has(s.concept) ? n.delete(s.concept) : n.add(s.concept); return n; })}
              className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 transition-colors ${off ? "opacity-40 border-[var(--aria-border)]" : "border-[var(--aria-border)] hover:border-[var(--aria-primary)]"}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: color, boxShadow: off ? "none" : `0 0 6px ${color}` }} />
              {s.concept}
              <span className="text-[var(--aria-text-muted)]">{s.gain > 0 ? `+${s.gain}` : `L${s.end}`}</span>
            </button>
          );
        })}
      </div>
      {series.length === 0 && <div className="text-sm text-[var(--aria-text-muted)] mt-4">No mastery evidence in this window yet — run an interview or connect a lecture.</div>}
    </div>
  );
}
